import { Select, rem } from '@mantine/core';
import { GridApi } from 'ag-grid-community';
import { RevisionRow } from 'components/tables/data/revision/types';
import useFilterValue, { FilterChangeCallback } from 'components/tables/filters/hooks/use-filter-value';
import { useEffect, useMemo, useState } from 'react';

/**
 * The key of the filter. Must be equal to the column field.
 */
export const FILTER_KEY = 'safetyPointLabel' as const;

/**
 * Parameters of the RevisionSafetyPointSelectFilter component.
 */
interface RevisionSafetyPointSelectFilterProps {
  api: GridApi<RevisionRow>;
  onChange?: FilterChangeCallback;
}

/**
 * Filter component for the revision safetyPoint column.
 *
 * This filter assumes:
 *  - column field is `safetyPoint`
 */
export default function RevisionSafetyPointSelectFilter({ api, onChange }: RevisionSafetyPointSelectFilterProps) {
  const [value, setValue] = useFilterValue({ api, filterKey: FILTER_KEY, onChange });
  const [labels, setLabels] = useState<string[]>([]);

  const options = useMemo(() => labels.map((label) => ({ value: label, label })), [labels]);

  useEffect(() => {
    /** Finds unique safetyPointLabel values visible in the table and exposes them as filter options. */
    const findUniqueLabels = () => {
      const uniqueLabels = new Set<string>();

      api.forEachNode(({ displayed, data }) => {
        if (!displayed || !data?.safetyPointLabel) {
          return;
        }

        uniqueLabels.add(data.safetyPointLabel);
      });

      setLabels(Array.from(uniqueLabels).sort((a, b) => a.localeCompare(b)));
    };

    findUniqueLabels();

    api.addEventListener('modelUpdated', findUniqueLabels);
    return () => api.removeEventListener('modelUpdated', findUniqueLabels);
  }, [api]);

  return (
    <Select
      clearable
      searchable
      w="100%"
      size="sm"
      placeholder="Všetky typy záveru"
      data={options}
      value={value}
      onChange={setValue}
      styles={{ dropdown: { minWidth: rem(240) } }}
    />
  );
}
